import { useContext } from 'react'
import StickyFooter from './StickyFooter'
import themeConfig from '../../../theme.config'
import BackToTop from './BackToTop'
import { lightOrDark } from '../../helpers/style'
import { MarkupContext } from '../../providers/Markup'
import Logo from './Logo'
import { dataLayerPush } from '../../helpers/thirdparty'

function Link({ className, data, category = '' }) {
  const { detectPaletteClass } = useContext(MarkupContext)
  const lightOrDarkBase = lightOrDark(themeConfig.settings.nav.footer.base)
  if (!data.url) {
    return (
      <span className={className}>
        {data.label}
      </span>
    )
  }
  return (
    <a
      className={
        (themeConfig.settings.nav.footer.inheritLinkStyle
          ? ''
          : detectPaletteClass(`hover:underline   hover:text-${lightOrDarkBase}-500-hover`)) +
        (className ? ' ' + className : '')
      }
      href={data.url}
      target={data.target || '_self'}
      onClick={() => {
        dataLayerPush({
          event: 'navigation_footer_click',
          click_text: data.label,
          category: category !== '' ? category : data.label,
        })
      }}
    >
      {data.label}
    </a>
  )
}

function Footer(props) {
  const { createMarkup, detectPaletteClass } = useContext(MarkupContext)
  const lightOrDarkBase = lightOrDark(themeConfig.settings.nav.footer.base)

  return (
    <>
      <footer
        style={{
          '--light-or-dark': lightOrDarkBase,
        }}
        className={detectPaletteClass(`py-16 bg-(--sb-footer-bg) text-${lightOrDarkBase}`)}
      >
        <div className="flex flex-col items-start gap-12 w-full max-w-7xl mx-auto px-8">
          {/* TOP ROW: Logo and Social Icons */}
          <div className="flex flex-row justify-between items-center w-full gap-4 lg:gap-8 lg:px-6">
            <div className="flex justify-start **:data-[type=siteLogo]:basis-auto [&_[data-type=siteLogo] a]:justify-start">
              <Logo deskLogo={props.deskLogo} mobLogo={props.mobLogo} position="footer" />
            </div>

            {props.socialMediaLinks?.length > 0 && (
              <ul className="flex justify-end gap-4 lg:gap-10 items-center">
                {props.socialMediaLinks.map((link, j) => {
                  let domain = ''

                  try {
                    domain = link.link?.url ? new URL(link.link.url).hostname.replace(/^www\./, '') : ''
                  } catch (e) {
                    domain = ''
                  }

                  return (
                    <li key={j}>
                      <a
                        href={link.link?.url}
                        target="_blank"
                        title={link.link?.name}
                        className="group inline-flex border-b-2 border-transparent pb-1 transition-all duration-500 hover:border-[currentColor]"
                        onClick={() => {
                          dataLayerPush({
                            event: 'social_outbound_click',
                            click_text: link.link?.name,
                            outbound_domain: domain,
                          })
                        }}
                      >
                        <img
                          className="h-(--footer-social-icon-size,24px) aspect-square object-contain group-hover:scale-[1.1] transition-all duration-500"
                          alt=""
                          src={link.linkImage?.src}
                        />
                        <span className="sr-only">{link.link?.name}</span>
                      </a>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          {/* Multi-Column Navigation */}
          {props.footerNavigationItems?.length > 0 && (
            <div className="w-full grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
              {props.footerNavigationItems.map((col, idx) => (
                <nav
                  key={idx}
                  className={idx === 4 ? 'col-span-2 md:col-span-1' : ''}
                  aria-label={`Footer nav column ${idx + 1}`}
                >
                  <ul className="flex flex-col gap-6">
                    {(col.footerNavigationItems || [])
                      .filter((link) => link.label || link.url)
                      .map((link, j) => (
                        <li key={j} data-rte={themeConfig.settings.nav.footer.inheritLinkStyle.toString()}>
                          {/* Column Header */}
                          <Link
                            data={link}
                            className="font-bold text-sm !text-(--sb-footer-text) leading-5 block mb-3"
                          />

                          {/* Column Children Links */}
                          {(link.children || []).filter((childLink) => childLink.url).length > 0 && (
                            <ul className="flex flex-col gap-2">
                              {(link.children || [])
                                .filter((childLink) => childLink.url)
                                .map((childLink, k) => (
                                  <li key={k}>
                                    <Link
                                      data={childLink}
                                      className="text-sm font-normal !text-(--sb-footer-text) leading-5"
                                      category={link.label}
                                    />
                                  </li>
                                ))}
                            </ul>
                          )}
                        </li>
                      ))}
                  </ul>
                </nav>
              ))}
            </div>
          )}

          {/* Footnotes, Copyright, Disclaimer */}
          {(props.copyrightText || props.footerDescription || props.footerDisclaimerText) && (
            <div className="w-full flex flex-col gap-3 mt-4 pt-4 text-center md:text-right">
              {props.copyrightText && props.copyrightText !== '<p></p>' && (
                <div className="!text-(--sb-footer-text) text-sm font-normal leading-[18px]">
                  <div data-rte="true" dangerouslySetInnerHTML={createMarkup(props.copyrightText)} />
                </div>
              )}
              {props.footerDescription && props.footerDescription !== '<p></p>' && (
                <div className="!text-(--sb-footer-text) text-xs font-extralight">
                  <div data-rte="true" dangerouslySetInnerHTML={createMarkup(props.footerDescription)} />
                </div>
              )}
              {props.footerDisclaimerText && props.footerDisclaimerText !== '<p></p>' && (
                <div className="!text-(--sb-footer-text) text-xs font-extralight">
                  <div data-rte="true" dangerouslySetInnerHTML={createMarkup(props.footerDisclaimerText)} />
                </div>
              )}
            </div>
          )}

          {/* BackToTop is fixed-position — rendered here so it mounts, but does not affect footer layout */}
          <BackToTop />
        </div>
      </footer>
      {!props.disableSticky && (props.stickyNavPrimary || props.stickyNavSecondary) && <StickyFooter {...props} />}
    </>
  )
}

export default Footer
